// Pagos del vendedor: su saldo en el libro, por qué lo pendiente no ha caído y el estado de cada
// depósito. Sale de `GET /commissions/me/payments`, dicho con las palabras del vendedor.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionPaymentAttentionEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPaymentAttentionEnum';
import { CommissionPaymentStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPaymentStatusEnum';

type Balance = RetailCommission.CommissionSellerBalance;
type Payment = RetailCommission.CommissionPayment;

/** Los rechazos del motor de cobro que el vendedor puede entender. */
const REJECTION_TEXT: Partial<Record<string, string>> = {
    ACCOUNT_NOT_FOUND: 'No encontramos tu wallet',
    USER_NOT_ACTIVE: 'Tu cuenta está bloqueada',
    ACCOUNT_LEVEL_MAX_MONTHLY_AMOUNT_EXCEEDED: 'Tu wallet llegó al tope de su nivel',
    ACCOUNT_LEVEL_MAX_AVAILABLE_BALANCE_EXCEEDED: 'Tu wallet llegó al tope de su nivel',
};

/** Por qué la wallet no recibió el depósito. */
function rejectionText(code: string | null): string {
    return (code === null ? undefined : REJECTION_TEXT[code]) ?? 'Tu wallet rechazó el depósito';
}

/** Por qué lo pendiente no se ha depositado; `null` si nada lo detiene. La deuda va aparte. */
export function attentionText(
    balance: Pick<Balance, 'attention' | 'attentionCode'>,
): string | null {
    switch (balance.attention) {
        case CommissionPaymentAttentionEnum.NO_WALLET:
            return 'Aún no tienes una wallet vinculada';
        case CommissionPaymentAttentionEnum.REJECTED:
            return rejectionText(balance.attentionCode);
        case CommissionPaymentAttentionEnum.PAUSED:
            return 'Tu cadena pausó los depósitos';
        // La deuda tiene su tarjeta.
        case CommissionPaymentAttentionEnum.DEBT:
        case null:
            return null;
        default:
            // Un motivo nuevo del motor no se inventa.
            balance.attention satisfies never;
            return null;
    }
}

export interface BalanceView {
    /** Lo que ya cayó a la wallet. */
    depositedCents: number;
    /** Lo que la cadena le pagó por fuera de la wallet. */
    paidOutsideCents: number;
    /** Exigible sin depositar; cero si debe. */
    payableCents: number;
    /** Apartado para un depósito en curso o sin confirmar. */
    inTransitCents: number;
    /** El exigible negativo, en positivo. */
    debtCents: number;
    /** Ganado que se paga el día 1. */
    retainedCents: number;
    /** Por qué lo pendiente no ha caído. */
    reason: string | null;
    /** Desde cuándo tiene ese motivo o, si debe, la deuda. */
    since: string | null;
}

/** El saldo para las tarjetas; sin libro todavía, todo en cero. */
export function balanceViewOf(balance: Balance | null): BalanceView {
    if (!balance) {
        return {
            depositedCents: 0,
            paidOutsideCents: 0,
            payableCents: 0,
            inTransitCents: 0,
            debtCents: 0,
            retainedCents: 0,
            reason: null,
            since: null,
        };
    }
    const debtCents = Math.max(-balance.payableCents, 0);
    const reason = attentionText(balance);
    const dated =
        debtCents > 0 ? balance.attention === CommissionPaymentAttentionEnum.DEBT : reason !== null;
    return {
        depositedCents: balance.accreditedCents,
        paidOutsideCents: balance.paidOutsideCents,
        payableCents: Math.max(balance.payableCents, 0),
        inTransitCents: balance.inTransitCents,
        debtCents,
        retainedCents: balance.retainedCents,
        reason,
        since: dated ? balance.attentionSince : null,
    };
}

export type PaymentTone = 'success' | 'info' | 'warning' | 'danger';

export interface PaymentStatusView {
    label: string;
    tone: PaymentTone;
    note: string | null;
    /** No se depositó: el monto no lleva "+". */
    failed: boolean;
}

/** El estado de un depósito como lo lee el vendedor. */
export function paymentStatusOf(payment: Pick<Payment, 'status' | 'errorCode'>): PaymentStatusView {
    switch (payment.status) {
        case CommissionPaymentStatusEnum.ACCREDITED:
            return { label: 'Depositado', tone: 'success', note: null, failed: false };
        case CommissionPaymentStatusEnum.PENDING:
            return { label: 'En camino', tone: 'info', note: null, failed: false };
        case CommissionPaymentStatusEnum.UNVERIFIED:
            return {
                label: 'En revisión',
                tone: 'warning',
                note: 'Estamos confirmando si llegó',
                failed: false,
            };
        case CommissionPaymentStatusEnum.FAILED:
            return {
                label: 'No se depositó',
                tone: 'danger',
                note:
                    payment.errorCode === null
                        ? 'Se suma a tu siguiente pago'
                        : `${rejectionText(payment.errorCode)} · se suma a tu siguiente pago`,
                failed: true,
            };
        default:
            // Un estado nuevo del motor no tumba la pantalla.
            payment.status satisfies never;
            return { label: 'En revisión', tone: 'warning', note: null, failed: false };
    }
}
